import React from 'react';
import { FormControl, Select, MenuItem, Tooltip } from '@mui/material';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import "./MultiCategoryFilter.css";
import "./MultiCategoryFilterV2.css";

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

interface FilterConfig {
  id: string;
  label: string;
  options: FilterOption[];
  selectedOptions: string[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<string[]>>;
}

interface MultiCategoryFilterProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  activeFilter: string;
  setActiveFilter: (id: string) => void;
  filters: FilterConfig[];
  label?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  showSearch?: boolean;
  onFilterChange?: () => void;
  /** optional: how many status buttons to show inline */
  inlineStatusCount?: number;
  showMoreFilters?: boolean;
}

const MultiCategoryFilterV2: React.FC<MultiCategoryFilterProps> = ({
  isOpen,
  setIsOpen,
  activeFilter,
  setActiveFilter,
  filters,
  label = "More Filters",
  searchValue = "",
  onSearchChange,
  showSearch = true,
  onFilterChange,
  inlineStatusCount = 4,
  showMoreFilters,
}) => {
  const handleOptionChange = (filterId: string, optionId: string, isChecked: boolean) => {
    const filter = filters.find(f => f.id === filterId);
    if (!filter) return;

    filter.setSelectedOptions(prev =>
      isChecked
        ? [...prev, optionId]
        : prev.filter(id => id !== optionId)
    );

    onFilterChange?.();
  };

  const handleClearAll = () => {
    filters.forEach(filter => {
      filter.setSelectedOptions([]);
    });
    if (onSearchChange) {
      onSearchChange("");
    }
    onFilterChange?.();
  };

  const handleFilterTypeChange = (filterId: string) => {
    if (onSearchChange) {
      onSearchChange("");
    }
    setActiveFilter(filterId);
  };

  // status filter (falls back to first filter if status not present)
  const statusFilter = filters.find(f => f.id === "status") || filters[0];

  const currentFilter = filters.find(f => f.id === activeFilter) || filters[0];
  const hasSelections = filters.some(f => f.selectedOptions.length > 0) || (searchValue?.length > 0);

  const isStatusSelected = (statusId: string) => {
    const sf = filters.find(f => f.id === "status");
    return !!sf && sf.selectedOptions.includes(statusId);
  };

  return (
    <div className='MultiCategoryFilterV2_container'>
      {/* Inline status buttons */}
      {statusFilter && (
        <div className="order-status-filters" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div
            className="order-status-button"
            onClick={(e) => {
              e.stopPropagation()
              handleClearAll();
            }}
            style={{
              backgroundColor: hasSelections ? "#FAFAFB" : "#00A392",
            }}
          >
            <span style={{
              color: hasSelections ? "#7c7c7c" : "white",
            }}>All</span>
          </div>

          {statusFilter.options
            .slice(0, inlineStatusCount)
            .map(opt => (
              <div
                key={opt.id}
                className="order-status-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOptionChange("status", opt.id, !isStatusSelected(opt.id));
                }}
                style={{
                  backgroundColor: isStatusSelected(opt.id) ? "#00A392" : "#FAFAFB",
                }}
              >
                <span style={{
                  color: isStatusSelected(opt.id) ? "white" : "#7c7c7c",
                  textTransform: "capitalize",
                  whiteSpace: "nowrap",
                }}>{opt.label}</span>
              </div>
            ))}
        </div>
      )}

      {showMoreFilters && 
        <FormControl className="manage_secondary_orders_filter" sx={{minWidth: "9.1em", maxWidth: "9.1em",
          "& .MuiInputBase-root": {
            fontSize: "inherit !important",
            height: "2.6em"
          },
        }}>
          <Select
            variant="outlined"
            labelId="dropdown_label"
            open={isOpen}
            value={label}
            onOpen={() => {
              setIsOpen(true);
            }}
            onClose={() => setIsOpen(false)}
            renderValue={() => (
              <span style={{ color: hasSelections ? "white" : "#7c7c7c", fontSize: "1em", fontWeight: 500 }}>
                {label}
              </span>
            )}
            sx={{
              background: hasSelections
                ? "#00A392"
                : "#FAFAFB",
              color: hasSelections ? "white" : "#7c7c7c",
              boxSizing: "border-box",
              height: "30px",
              width: "100%",
              borderRadius: "20px",
              "& .MuiInputBase-root": {
                fontSize: "inherit !important"
              },
              "& .MuiSelect-select": {
                fontSize: "inherit",
                height: "30px",
                padding: "6px 9px",
                paddingRight: "0px !important",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "#909090",
                outline: "none",
                border: "none",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",   // removes border
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiOutlinedInput-root.Mui-focused": {
                outline: "none",
                boxShadow: "none",
              },
              "& .MuiSelect-icon": {
                fontSize: "1em",
                color: hasSelections
                ? "white"
                : "#7C7C7C",
                right: "3px",
              },
            }}
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "right",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "right",
              },
              PaperProps: {
                sx: {
                  boxShadow: "none",
                  marginTop: "4px",
                  border: "1px solid #DEDEDE",
                },
              },
              MenuListProps: {
                sx: {
                  paddingTop: "8px",
                  paddingBottom: "0",
                },
              },
            }}
          >
            <MenuItem
              key={`filter-${activeFilter}-${filters.map(f => f.selectedOptions.join(','))}`}
              disableRipple
              style={{
                padding: 0,
                backgroundColor: "transparent",
                outline: "none",
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div
                className="MSO_filter-dropdown-container"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="MSO_filter-header">
                  <span className="MSO_filter-name">All Filters</span>
                  <span
                    className={`MSO_filter-clear-all ${(filters.some(f => f.selectedOptions.length > 0) || searchValue) &&
                      `MSO_filter-clear-all_active`
                      }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearAll();
                    }}
                  >
                    Clear All
                  </span>
                </div>

                {showSearch && (
                  <div className="MSO_filter_searchBar_container">
                    <div />
                    <input
                      className="MSO_fiter_searchBar"
                      placeholder="Search..."
                      value={searchValue}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      onChange={(e) => onSearchChange?.(e.target.value)}
                      autoFocus
                    />
                  </div>
                )}

                <div className="MSO_filter-options">
                  <div className="MSO_filter-options-left">
                    {filters.map((filter) => (
                      <div
                        key={filter.id}
                        className={`${activeFilter === filter.id ? `MSO_filterSelected` : ''}`}
                        onClick={(e) => {
                          handleFilterTypeChange(filter.id);
                          e.stopPropagation();
                        }}
                      >
                        {filter.label} ({(filter.selectedOptions || []).length})
                      </div>
                    ))}
                  </div>
                  <div className="MSO_filter-options-right">
                    <SimpleBar className="MSO_filter-option-secondary">
                      {currentFilter.options
                        .filter(option => {
                          if (!searchValue) return true;
                          const searchTerm = searchValue.toLowerCase().replace(/\s+/g, '');
                          const optionText = option.label.toLowerCase().replace(/\s+/g, '');
                          return optionText.includes(searchTerm);
                        })
                        .map((option) => (
                          <label
                            key={option.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              color: "#444444",
                              fontSize: "13px",
                              cursor: "pointer",
                              width: "215px",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={(currentFilter.selectedOptions || []).includes(option.id)}
                              onChange={(e) =>
                                handleOptionChange(currentFilter.id, option.id, e.target.checked)
                              }
                              style={{
                                borderColor: "#CBCBCB",
                                cursor: "pointer",
                                height: "16px",
                                width: "14px"
                              }}
                            />
                            <span
                              title={option.label}
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                              }}
                            >
                              {`${option.label}`}
                            </span>
                            {`(${option.count})`}
                          </label>
                        ))}
                    </SimpleBar>
                  </div>
                </div>
              </div>
            </MenuItem>
          </Select>
        </FormControl>
      }
    </div>
  );
};

export default MultiCategoryFilterV2;
